import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Lock, Save, Building2, MapPin, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const VertriebProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    region: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: ''
  });

  const token = localStorage.getItem('salesperson_token');
  const salespersonId = localStorage.getItem('salesperson_id');

  useEffect(() => {
    if (!token || !salespersonId) {
      navigate('/vertrieb/login');
      return;
    }
    loadProfile();
  }, [token, salespersonId, navigate]);

  const loadProfile = async () => {
    try {
      // Use auth/me endpoint for self-service profile
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/auth/me`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
          email: data.email || '',
          phone: data.phone || '',
          region: data.region || ''
        });
      } else if (response.status === 401) {
        navigate('/vertrieb/login');
      }
    } catch (error) {
      console.error('Load profile error:', error);
      toast.error('Profil konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name und E-Mail sind erforderlich');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/${salespersonId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.phone,
            region: formData.region
          })
        }
      );
      
      if (response.ok) {
        toast.success('Profil gespeichert');
        localStorage.setItem('salesperson_name', formData.name);
        await loadProfile();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Speichern fehlgeschlagen');
      }
    } catch (error) {
      console.error('Save profile error:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    
    if (passwordData.new_password !== passwordData.new_password_confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/${salespersonId}/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            current_password: passwordData.current_password,
            new_password: passwordData.new_password
          })
        }
      );
      
      if (response.ok) {
        toast.success('Passwort geändert');
        setPasswordData({ current_password: '', new_password: '', new_password_confirm: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Passwort ändern fehlgeschlagen');
      }
    } catch (error) {
      console.error('Change password error:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/vertrieb/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <h1 className="text-xl font-bold text-gray-900">Mein Profil</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Info */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-green/10 rounded-xl flex items-center justify-center">
              <User className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Persönliche Daten</h2>
              <p className="text-sm text-gray-600">Ihre Kontaktinformationen</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
                data-testid="profile-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">E-Mail</label>
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">E-Mail kann nicht geändert werden</p>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="+49 ..."
                data-testid="profile-phone-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Region</label>
              <input
                type="text"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="z.B. Hamburg, Norddeutschland"
                data-testid="profile-region-input"
              />
            </div>
          </div>

          <div className="mt-6">
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="bg-brand-green hover:bg-brand-green-dark text-white"
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Änderungen speichern'}
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Meine Statistiken</h2>
              <p className="text-sm text-gray-600">Ihre Performance-Übersicht</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{profile?.total_leads || 0}</p>
              <p className="text-sm text-gray-600">Kontakte</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{profile?.converted_leads || 0}</p>
              <p className="text-sm text-gray-600">Konvertiert</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{profile?.total_tours || 0}</p>
              <p className="text-sm text-gray-600">Touren</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">
                {profile?.total_leads > 0 
                  ? Math.round((profile?.converted_leads || 0) / profile.total_leads * 100) 
                  : 0}%
              </p>
              <p className="text-sm text-gray-600">Quote</p>
            </div>
          </div>
        </div>

        {/* Password Change */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
              <Lock className="w-7 h-7 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Passwort ändern</h2>
              <p className="text-sm text-gray-600">Mindestens 8 Zeichen</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Aktuelles Passwort *</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Neues Passwort *</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Neues Passwort wiederholen *</label>
              <input
                type="password"
                value={passwordData.new_password_confirm}
                onChange={(e) => setPasswordData({ ...passwordData, new_password_confirm: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          </div>

          <div className="mt-6">
            <Button
              onClick={handleChangePassword}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Lock className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Passwort ändern'}
            </Button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Hinweis</p>
              <p>Bei Fragen zu Ihrem Account oder zur Provisionsabrechnung wenden Sie sich bitte an die Zentrale unter 04101 847 11 60.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VertriebProfilePage;
